import type { SelectedEntity } from '@common/types/layout.type';
import { DeviceMode, type DrawerState, EDrawerType, PreviewMode, type PanelPosition } from '@common/types/layout.type';

import { createReducer, on } from '@ngrx/store';

import LayoutActions from './layout.actions';

export const LAYOUT_FEATURE_KEY = 'layout';

export type LayoutState = {
    selectedEntity: SelectedEntity;
    previewMode: PreviewMode;
    deviceMode: DeviceMode;
    isEntityDragging: boolean;
    isColumnWidthChanging: boolean;
    drawerState: DrawerState;
    isHideColumnControls: boolean;
    settingsPanelPosition: PanelPosition;
    /** Mirrors `AiSessionService.isActive` — see §7's "preview mode" (`ai-mcp-plan.md`). */
    isAiReplyActive: boolean;
    /** Mirrors `ElementPickerService.active` — the two OR together into `selectIsCanvasPreviewActive`. */
    isElementPickerActive: boolean;
};

export const initialLayoutState: LayoutState = {
    selectedEntity: null,
    previewMode: null,
    deviceMode: DeviceMode.desktop,
    drawerState: {
        blockIndex: null,
        drawerType: null,
    },
    isEntityDragging: false,
    isColumnWidthChanging: false,
    isHideColumnControls: false,
    settingsPanelPosition: { x: 0, y: 0 },
    isAiReplyActive: false,
    isElementPickerActive: false,
};

export const layoutReducer = createReducer<LayoutState>(
    initialLayoutState,
    on(LayoutActions.setSelectedEntity, (state: LayoutState, { selectedEntity }) => {
        const showSettings = 'showSettings' in selectedEntity ? selectedEntity?.showSettings : true;
        return { ...state, selectedEntity: { ...selectedEntity, showSettings } };
    }),
    on(LayoutActions.hideSelectedEntitySettings, (state: LayoutState) => {
        if (!state.selectedEntity) return state;

        return {
            ...state,
            selectedEntity: {
                ...state.selectedEntity,
                showSettings: false,
            },
        };
    }),
    on(LayoutActions.unsetEntity, (state: LayoutState) => ({ ...state, selectedEntity: null })),

    on(LayoutActions.showPreview, (state: LayoutState) => ({ ...state, previewMode: PreviewMode.desktop })),
    on(LayoutActions.changePreviewMode, (state: LayoutState, { previewMode }) => ({ ...state, previewMode })),
    on(LayoutActions.hidePreview, (state: LayoutState) => ({ ...state, previewMode: null })),

    on(LayoutActions.changeDeviceMode, (state: LayoutState, { deviceMode }) => ({ ...state, deviceMode })),

    on(LayoutActions.openDrawer, (state: LayoutState, { drawerState }) => ({ ...state, drawerState })),
    on(LayoutActions.closeDrawer, (state: LayoutState) => ({ ...state, drawerState: { drawerType: null } })),

    on(LayoutActions.setIsEntityDragging, (state: LayoutState, { isEntityDragging }) => ({
        ...state,
        isEntityDragging,
    })),
    on(LayoutActions.setColumnWidthChanging, (state: LayoutState, { isColumnWidthChanging }) => ({
        ...state,
        isColumnWidthChanging,
    })),
    on(LayoutActions.togglePageDrawer, (state: LayoutState) => ({
        ...state,
        drawerState: {
            ...state.drawerState,
            drawerType: state.drawerState.drawerType === EDrawerType.page ? null : EDrawerType.page,
        },
    })),
    on(LayoutActions.setIsHideColumnControls, (state: LayoutState, { isHideColumnControls }) => ({
        ...state,
        isHideColumnControls,
    })),
    on(LayoutActions.setSettingsPanelPosition, (state: LayoutState, { x, y }) => ({
        ...state,
        settingsPanelPosition: { x, y },
    })),
    on(LayoutActions.setAiReplyActive, (state: LayoutState, { active }) => ({ ...state, isAiReplyActive: active })),
    on(LayoutActions.setElementPickerActive, (state: LayoutState, { active }) => ({
        ...state,
        isElementPickerActive: active,
    }))
);
